import React, { useRef, useEffect, useState } from 'react';
import { SelectWithLabel } from '../ui/universal-select';
import { useDrag, useDrop } from 'react-dnd';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { ReactComponent as GripVerticalIcon } from '../../assets/icons/grip-vertical.svg';
import { ReactComponent as RotateCcwIcon } from '../../assets/icons/rotate-ccw.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/lightning-icon.svg';
import { FlashlightLineIcon } from '../ui/figma-icons-v5';
import { Checkbox } from './components/checkbox';

interface QuestionCardProps {
  question: {
    id: string;
    text: string;
    evaluationCriteria: string;
    isRequired: boolean;
    order: number;
    isVoiced?: boolean; // Добавляем поле для озвучивания
  };
  index: number;
  questions: Array<{
    id: string;
    text: string;
    evaluationCriteria: string;

    isRequired: boolean;
    order: number;
    isVoiced?: boolean; // Добавляем поле для озвучивания
  }>;
  setQuestions: React.Dispatch<React.SetStateAction<Array<{
    id: string;
    text: string;
    evaluationCriteria: string;

    isRequired: boolean;
    order: number;
    isVoiced?: boolean; // Добавляем поле для озвучивания
  }>>>;
  moveQuestion: (dragIndex: number, hoverIndex: number) => void;
  onRegenerateQuestion: (questionIndex: number, currentQuestion: any) => Promise<void>;
  onDeleteQuestion: (questionIndex: number) => void;
  onShowPreviousVariant: (questionIndex: number) => void;
  onShowNextVariant: (questionIndex: number) => void;
}

export function QuestionCard({
  question,
  index,
  questions,
  setQuestions,
  moveQuestion,
  onRegenerateQuestion,
  onDeleteQuestion,
  onShowPreviousVariant,
  onShowNextVariant
}: QuestionCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const colors = useBrandingColors();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [questionWeight, setQuestionWeight] = useState('');

  // Drag and Drop logic
  const [{ isDragging }, drag] = useDrag({
    type: 'question',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'question',
    hover: (item: { index: number }, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Time to actually perform the action
      moveQuestion(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Auto-scroll functionality
  useEffect(() => {
    if (!isDragging || !ref.current) return;

    const scrollSpeed = 15; // Скорость скролла в пикселях
    const scrollThreshold = 100; // Расстояние от края экрана для активации скролла

    const handleScroll = () => {
      if (!ref.current) return;
      
      const element = ref.current;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementBottom = rect.bottom;

      // Скролл вверх (когда карточка близко к верхнему краю)
      if (elementTop < scrollThreshold) {
        const scrollAmount = Math.max(1, Math.floor((scrollThreshold - elementTop) / 10));
        window.scrollBy(0, -scrollAmount);
      }
      // Скролл вниз (когда карточка близко к нижнему краю)
      else if (elementBottom > viewportHeight - scrollThreshold) {
        const scrollAmount = Math.max(1, Math.floor((elementBottom - (viewportHeight - scrollThreshold)) / 10));
        window.scrollBy(0, scrollAmount);
      }
    };

    const scrollInterval = setInterval(handleScroll, 16); // ~60fps

    return () => clearInterval(scrollInterval);
  }, [isDragging]);

  // Connect drag and drop refs
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`bg-[#ffffff] rounded-[12px] p-4 w-full relative transition-all duration-300 cursor-move ${
        isDragging ? 'opacity-50 scale-95' : ''
      }`}
      style={isOver && !isDragging ? { borderColor: colors.primary, backgroundColor: '#fef7f6' } : undefined}
    >
      {/* Question header with number and drag handle */}
      <div className="flex items-center gap-[10px] mb-4">
        <div className="cursor-move">
          <GripVerticalIcon className="w-6 h-6" />
        </div>
        <h4 className="text-[20px] font-medium text-[#0a0d14] leading-[28px]">
          Вопрос {index + 1} из {questions.length}
        </h4>
      </div>

      {/* Question input */}
      <div className="mb-4">
        <textarea
          value={question.text}
          onChange={(e) => {
            setQuestions(prev => prev.map((q, i) => 
              i === index ? { ...q, text: e.target.value } : q
            ));
          }}
          placeholder="Введите текст вопроса"
          className="w-full h-[120px] py-[14px] px-[24px] border border-[#e2e4e9] rounded-[16px] text-[16px] text-[16px] resize-none outline-none focus:ring-0"
          onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}33`; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
        />
      </div>

      {/* Actions - buttons under first input */}
      <div className="flex items-center justify-between mb-4 px-6">
        <div className="flex items-center gap-[8px]">
          <button
            onClick={() => onShowPreviousVariant(index)}
            className="flex items-center justify-center w-8 h-8 transition-colors hover:bg-[#f6f8fa] rounded"
            title="К предыдущему вопросу"
          >
            <RotateCcwIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onShowNextVariant(index)}
            className="flex items-center justify-center w-8 h-8 transition-colors hover:bg-[#f6f8fa] rounded"
            title="К следующему вопросу"
          >
            <RotateCcwIcon className="w-4 h-4 scale-x-[-1]" />
          </button>
        </div>
        
        <div className="flex items-center gap-4">
        <button
          onClick={async () => {
            try {
              setIsRegenerating(true);
              await onRegenerateQuestion(index, question);
            } finally {
              setIsRegenerating(false);
            }
          }}
          className="flex items-center gap-1 transition-colors text-[14px] font-normal disabled:opacity-60"
          style={{ 
            background: 'linear-gradient(180deg, rgba(225, 99, 73, 1) 44%, rgba(223, 28, 65, 1) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
          disabled={isRegenerating}
          title="Сгенерировать снова"
        >
          <SparklesIcon className="w-4 h-4" style={{ color: 'rgba(225, 99, 73, 1)' }} />
          {isRegenerating ? 'Генерируем…' : 'Сгенерировать снова'}
        </button>

        <button
          onClick={() => onDeleteQuestion(index)}
          className="transition-colors text-[14px] font-normal"
          style={{ 
            background: 'linear-gradient(180deg, rgba(225, 99, 73, 1) 44%, rgba(223, 28, 65, 1) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
          title="Удалить вопрос"
        >
          Удалить
        </button>
        </div>
      </div>

      {/* Question settings: временно скрываем выбор обязательности, пока все вопросы всегда обязательные */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" />

      {/* Evaluation criteria */}
      <div className="mb-4">
        <textarea
          value={question.evaluationCriteria}
          onChange={(e) => {
            setQuestions(prev => prev.map((q, i) => 
              i === index ? { ...q, evaluationCriteria: e.target.value } : q
            ));
          }}
          placeholder="Укажите ключевые точки на которые ИИ должен опираться при оценке. Это не обязательно, но может улучшить точность оценки."
          className="w-full h-[80px] py-[14px] px-[24px] border border-[#e2e4e9] rounded-[16px] text-[14px] text-[#525866] resize-none outline-none focus:ring-2 placeholder:text-[#868c98]"
          onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}33`; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
        />
      </div>

      {/* Bottom row: Weight and Checkbox */}
      <div className="flex items-center justify-between px-6">
        <div className="flex items-center gap-5">
          
          
        </div>
        
        
      </div>
    </div>
  );
} 